import { useQuery } from '@tanstack/react-query'
import type { FormEvent, ReactElement, ReactNode } from 'react'
import { useEffect, useId, useState } from 'react'
import type { WarrantGrantView } from '@/api/warrants'
import { api } from '~/api/client'
import { chartCss } from '~/charts/theme'
import { EntityChoice } from '~/components/EntityChoice'
import { Button, SegmentedControl } from '~/components/ui/Controls'
import { Field, Modal, UnitField } from '~/components/ui/Modal'
import type { GrantDraft } from '~/state/warrants'
import { regimesIn, useWarrantTaxRates } from '~/state/warrant-tax'
import {
  REGIME_ORDER,
  draftFromGrant,
  emptyGrantDraft,
  refuseGrant,
  toGrantBody,
  toGrantEdit,
  regimeLabel,
  regimeNote,
  underlyingChoices,
  useEditWarrantGrant,
  useRemoveWarrantGrant,
  useSaveWarrantGrant,
  useWarrants,
} from '~/state/warrants'

export const GRANT_CSS = `
.nw-grant p { color: var(--nw-body-ink); font-size: var(--nw-text-cell); line-height: 1.55; margin: 0 0 var(--nw-space-3); max-width: 62ch; }
.nw-grant h3 { color: var(--nw-faint); font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); letter-spacing: var(--nw-track-label); margin: var(--nw-space-4) 0 var(--nw-space-3); text-transform: uppercase; }
.nw-grant h3:first-child { margin-top: 0; }
/* Two boxes to a row where there is room for two, and one where there is not —
   the dialog is 576 wide on a desktop and the width of a phone on a phone. */
.nw-grant .nw-grant-row { display: grid; gap: var(--nw-space-3); grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); margin-bottom: var(--nw-space-3); }
.nw-grant .nw-field small { color: var(--nw-muted); font-size: var(--nw-text-chip); }
.nw-grant .nw-error { color: var(--nw-critical); font-size: var(--nw-text-cell); margin: 0 0 var(--nw-space-3); }
.nw-grant .nw-said { background: var(--nw-hover); border-radius: var(--nw-radius-md); color: var(--nw-body-ink); font-size: var(--nw-text-cell); margin: 0 0 var(--nw-space-3); padding: 10px var(--nw-space-3); }
.nw-grant .nw-check { margin-bottom: var(--nw-space-3); }
.nw-grant textarea { background: var(--nw-ground); border: var(--nw-hairline-width) solid var(--nw-hairline); border-radius: var(--nw-field-radius); box-sizing: border-box; color: var(--nw-text); font: inherit; font-size: var(--nw-text-field); padding: 10px 14px; resize: vertical; width: 100%; }
/* The last row of the form is its footer: the modal's own foot slot sits
   outside the form, where a submit button would not reach it. */
.nw-grant .nw-grant-actions { align-items: center; border-top: var(--nw-hairline-width) solid var(--nw-hairline); display: flex; flex-wrap: wrap; gap: var(--nw-space-2); justify-content: flex-end; margin-top: var(--nw-space-2); padding-top: var(--nw-space-4); }
/* Removing a grant is not the other end of saving one, so it stands at the
   other end of the row from the button that keeps it. */
.nw-grant .nw-grant-remove { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-2); margin-right: auto; }
.nw-grant .nw-confirm { color: var(--nw-critical); font-size: var(--nw-text-cell); }
`

/**
 * The menu's last entry, standing for naming one rather than picking one.
 *
 * A single space, which is a value no regime can carry: the route trims a
 * regime and refuses an empty one, so this cannot collide with a name a
 * household has actually given something.
 */
const NAME_ANOTHER = ' '

/**
 * What the menu says about the choice, in the register the warrant rates card
 * uses for the same question: the two statutes are this household's, not a rule
 * of the world.
 */
function regimeHelp(draft: GrantDraft): string {
  if (draft.namingRegime) {
    return `The two above are the statutes this was built against, not the only kinds of warrant taxation there are. ${regimeNote(draft.regime.trim())}`
  }
  return draft.regime === ''
    ? 'Regimes are taxed differently on exercise, so the figures differ by which one this grant was written under. There is no default.'
    : regimeNote(draft.regime)
}

const TARGET_CHOICES = [
  { id: 'held', label: 'Already entered' },
  { id: 'new', label: 'Not entered yet' },
]

/**
 * A warrant grant, entered or corrected.
 *
 * Nothing here works out what a grant is worth. The spread, the tax on it and
 * the cash the exercise costs are one calculation over the schedule, the strike
 * and the round the underlying was last priced at, and the report renders the
 * server's answer — a second answer computed in this dialog would be a figure
 * that disagrees with the one beside it the moment either changes.
 */
export function WarrantGrantDialog({
  editing,
  switcher,
  onClose,
}: {
  /** The grant being corrected, absent while one is being entered. */
  editing?: WarrantGrantView
  /** The choice of what is being added, where this dialog is one of two. */
  switcher?: ReactNode
  onClose: () => void
}): ReactElement {
  const warrants = useWarrants()
  const holdings = useQuery({
    queryKey: ['holdings'],
    queryFn: () => api.holdings(),
    enabled: editing === undefined,
  })

  useEffect(() => {
    const opener = document.activeElement
    // Whatever opened the dialog gets the focus back. The element only does that
    // for itself when it is the one doing the closing, and this one is closed by
    // being taken off the page.
    return () => {
      if (opener instanceof HTMLElement) opener.focus()
    }
  }, [])

  const title = editing === undefined ? 'New warrant grant' : `Correct the ${editing.underlying.symbol} grant`

  // Entering a grant waits for both: the base currency the strike is assumed to
  // be in, and the companies a grant can already be filed against. Offered
  // before those arrive, the form would name a new company for one the
  // household already has, and a warrant and its shares would be priced off
  // two different rows.
  const waiting = editing === undefined && (warrants.data === undefined || holdings.data === undefined)
  const failure = editing === undefined ? (warrants.error ?? holdings.error) : null

  if (waiting) {
    return (
      <div className="nw-grant">
        <Modal title={title} labelledBy="nw-grant-title" onClose={onClose}>
          <style>{chartCss + GRANT_CSS}</style>
          {switcher}
          {failure ? (
            <p className="nw-error">Could not read what is already entered: {failure.message}</p>
          ) : (
            <p>Loading…</p>
          )}
        </Modal>
      </div>
    )
  }

  return (
    <GrantForm
      title={title}
      editing={editing}
      switcher={switcher}
      baseCurrency={warrants.data?.baseCurrency ?? ''}
      choices={underlyingChoices(holdings.data?.rows ?? [], warrants.data?.grants ?? [])}
      onClose={onClose}
    />
  )
}

function GrantForm({
  title,
  editing,
  switcher,
  baseCurrency,
  choices,
  onClose,
}: {
  title: string
  editing?: WarrantGrantView
  switcher?: ReactNode
  baseCurrency: string
  choices: ReturnType<typeof underlyingChoices>
  onClose: () => void
}): ReactElement {
  const [draft, setDraft] = useState<GrantDraft>(() => {
    if (editing !== undefined) return draftFromGrant(editing)
    const empty = emptyGrantDraft(baseCurrency)
    // Nothing entered to file a grant against leaves naming the company the only
    // way through, so the draft starts there rather than on a choice of none.
    return choices.length === 0 ? { ...empty, target: 'new' } : empty
  })
  const rates = useWarrantTaxRates()
  /**
   * The same list the rates card renders a box for: the built-ins, the ones
   * this household has priced, and the ones its own grants name. The form can
   * therefore never refuse a regime the card has taken a rate for, nor one an
   * earlier grant was filed under. The built-in order comes first so the two
   * Danish statutes stay where a household used to find them.
   */
  const offeredRegimes = regimesIn(rates.data?.regimes ?? REGIME_ORDER, rates.data?.versions ?? [])

  const [refusal, setRefusal] = useState<string | null>(null)
  const [matched, setMatched] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const field = useId()
  const save = useSaveWarrantGrant()
  const edit = useEditWarrantGrant()
  const remove = useRemoveWarrantGrant()

  const change = (patch: Partial<GrantDraft>): void => setDraft({ ...draft, ...patch })
  const pending = save.isPending || edit.isPending
  const failure = save.error ?? edit.error ?? remove.error

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    const refused = refuseGrant(draft, editing !== undefined)
    if (refused !== undefined) {
      setRefusal(refused)
      return
    }
    setRefusal(null)

    try {
      if (editing !== undefined) {
        edit.mutate({ id: editing.id, edit: toGrantEdit(draft) }, { onSuccess: onClose })
        return
      }
      save.mutate(toGrantBody(draft), {
        // A grant is identified by the company, the date and the strike, so
        // sending the same terms twice writes nothing the second time. Saying so
        // is the difference between a grant that was already there and a save
        // that did not happen.
        onSuccess: (saved) => (saved.created ? onClose() : setMatched(true)),
      })
    } catch (error) {
      setRefusal((error as Error).message)
    }
  }

  return (
    <div className="nw-grant">
      <Modal title={title} labelledBy="nw-grant-title" onClose={onClose}>
        <style>{chartCss + GRANT_CSS}</style>
        {switcher}
        <form onSubmit={submit} aria-label="Warrant grant">
          <p>
            A warrant is a right to buy shares at a fixed price, so nothing here says the household
            owns any and no holding is written. What the grant is worth is the spread on what has
            vested, net of the strike and the tax on exercising — the report works all three out
            from these terms once they are saved.
          </p>

          {editing === undefined ? (
            <NewUnderlying draft={draft} choices={choices} field={field} onChange={change} />
          ) : (
            <p className="nw-said">
              Over <b>{editing.underlying.symbol}</b> — {editing.underlying.name}. Which company a
              grant converts into is its identity, so it cannot be moved here; a grant over another
              company is a different grant.
            </p>
          )}

          <h3>The grant</h3>
          <div className="nw-grant-row">
            <EntityChoice
              label="Whose grant"
              placeholder="Choose…"
              value={draft.entityKey}
              required
              id={`${field}-entity`}
              onChange={(entityKey) => change({ entityKey })}
            />
            <Field id={`${field}-granted`} label="Granted on" width="set">
              <input
                id={`${field}-granted`}
                type="date"
                value={draft.grantedDate}
                onChange={(e) => change({ grantedDate: e.target.value })}
                required
              />
            </Field>
          </div>
          <div className="nw-grant-row">
            <Field id={`${field}-count`} label="Warrants granted" width="set">
              <input
                id={`${field}-count`}
                value={draft.count}
                onChange={(e) => change({ count: e.target.value })}
                inputMode="decimal"
                required
              />
            </Field>
            <Field id={`${field}-currency`} label="Currency" width="set">
              <input
                id={`${field}-currency`}
                value={draft.currency}
                onChange={(e) => change({ currency: e.target.value })}
                maxLength={3}
                required
              />
            </Field>
            <UnitField
              id={`${field}-strike`}
              label="Strike per warrant"
              unit={draft.currency.trim().toUpperCase() || baseCurrency}
            >
              <input
                id={`${field}-strike`}
                value={draft.strikeMajor}
                onChange={(e) => change({ strikeMajor: e.target.value })}
                inputMode="decimal"
                required
              />
            </UnitField>
          </div>

          <div className="nw-grant-row">
            <Field id={`${field}-regime`} label="Regime" width="set">
              <select
                id={`${field}-regime`}
                value={draft.namingRegime ? NAME_ANOTHER : draft.regime}
                onChange={(e) =>
                  change(
                    e.target.value === NAME_ANOTHER
                      ? { namingRegime: true, regime: '' }
                      : { namingRegime: false, regime: e.target.value },
                  )
                }
                required
              >
                <option value="">Choose…</option>
                {offeredRegimes.map((regime) => (
                  <option key={regime} value={regime}>
                    {regimeLabel(regime)}
                  </option>
                ))}
                <option value={NAME_ANOTHER}>Something else…</option>
              </select>
              <small>{regimeHelp(draft)}</small>
            </Field>
            {draft.namingRegime && (
              <Field id={`${field}-regime-name`} label="What it is called" width="set">
                <input
                  id={`${field}-regime-name`}
                  value={draft.regime}
                  onChange={(e) => change({ regime: e.target.value })}
                  required
                />
                <small>
                  Whatever this household knows it by. Nothing is configured by naming it here —
                  the name is part of the grant, and it is what the warrant rates card in Settings
                  offers a rate box against once the grant is saved.
                </small>
              </Field>
            )}
            <Field id={`${field}-expiry`} label="Expires on" width="set">
              <input
                id={`${field}-expiry`}
                type="date"
                value={draft.expiryDate}
                onChange={(e) => change({ expiryDate: e.target.value })}
              />
              <small>
                Left blank, the report says the expiry is not known — which is not the same as a
                grant that never expires.
              </small>
            </Field>
          </div>

          <h3>Vesting</h3>
          {editing !== undefined && (
            <>
              <p className="nw-said">
                On record: the cliff falls on {editing.cliffOn} and the last of them vest on{' '}
                {editing.fullyVestedOn}. The months behind those two dates are not in what the
                report answers with, so a schedule can only be replaced here, never adjusted.
              </p>
              <label className="nw-check">
                <input
                  type="checkbox"
                  checked={draft.restateVesting}
                  onChange={(e) => change({ restateVesting: e.target.checked })}
                />
                Replace the vesting schedule
              </label>
            </>
          )}
          {(editing === undefined || draft.restateVesting) && (
            <>
              <div className="nw-grant-row">
                <Field id={`${field}-vest-start`} label="Vesting starts" width="set">
                  <input
                    id={`${field}-vest-start`}
                    type="date"
                    value={draft.vesting.startDate}
                    onChange={(e) =>
                      change({ vesting: { ...draft.vesting, startDate: e.target.value } })
                    }
                    required
                  />
                </Field>
                <UnitField id={`${field}-vest-cliff`} label="Cliff" unit="months">
                  <input
                    id={`${field}-vest-cliff`}
                    value={draft.vesting.cliffMonths}
                    onChange={(e) =>
                      change({ vesting: { ...draft.vesting, cliffMonths: e.target.value } })
                    }
                    inputMode="numeric"
                    required
                  />
                </UnitField>
              </div>
              <div className="nw-grant-row">
                <UnitField id={`${field}-vest-duration`} label="Vests over" unit="months">
                  <input
                    id={`${field}-vest-duration`}
                    value={draft.vesting.durationMonths}
                    onChange={(e) =>
                      change({ vesting: { ...draft.vesting, durationMonths: e.target.value } })
                    }
                    inputMode="numeric"
                    required
                  />
                </UnitField>
                <UnitField id={`${field}-vest-interval`} label="A tranche every" unit="months">
                  <input
                    id={`${field}-vest-interval`}
                    value={draft.vesting.intervalMonths}
                    onChange={(e) =>
                      change({ vesting: { ...draft.vesting, intervalMonths: e.target.value } })
                    }
                    inputMode="numeric"
                    required
                  />
                </UnitField>
              </div>
              <p>
                A schedule is a start, a cliff and a length in whole months, with a tranche every so
                many months — 1 for monthly, 3 for quarterly, 12 for yearly. Nothing vests before
                the cliff, and a part-vested tranche counts as none of it. A schedule with dates of
                its own, or tranches of different sizes, cannot be entered here.
              </p>
            </>
          )}

          <h3>Anything else</h3>
          <Field id={`${field}-note`} label="Note" width="set">
            <textarea
              id={`${field}-note`}
              rows={2}
              value={draft.note}
              onChange={(e) => change({ note: e.target.value })}
            />
          </Field>

          {matched && (
            <p className="nw-said">
              A grant over this company, granted on that date at that strike, is already entered.
              Nothing was written — the report shows the one that was there.
            </p>
          )}
          {refusal !== null && <p className="nw-error">{refusal}</p>}
          {failure && <p className="nw-error">{failure.message}</p>}

          <div className="nw-grant-actions">
            {editing !== undefined && (
              <span className="nw-grant-remove">
                {confirming ? (
                  <>
                    <span className="nw-confirm">Remove for good?</span>
                    <Button
                      kind="danger"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(editing.id, { onSuccess: onClose })}
                    >
                      Remove
                    </Button>
                    <Button onClick={() => setConfirming(false)}>Keep</Button>
                  </>
                ) : (
                  <Button kind="danger" onClick={() => setConfirming(true)}>
                    Remove the grant
                  </Button>
                )}
              </span>
            )}
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" kind="primary" disabled={pending}>
              {pending ? 'Saving…' : editing === undefined ? 'Save grant' : 'Save the correction'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

/**
 * The company the warrants convert into. Either one the household has already
 * entered or one it has not, because those are the two shapes the route takes
 * and it refuses a request that gives both.
 */
function NewUnderlying({
  draft,
  choices,
  field,
  onChange,
}: {
  draft: GrantDraft
  choices: ReturnType<typeof underlyingChoices>
  field: string
  onChange: (patch: Partial<GrantDraft>) => void
}): ReactElement {
  const nothingEntered = choices.length === 0
  const target = draft.target

  return (
    <>
      <h3>What they convert into</h3>
      {nothingEntered ? (
        <p className="nw-said">
          Nothing is entered for a grant to be filed against yet, so the company is named here.
        </p>
      ) : (
        <div className="nw-grant-row">
          <SegmentedControl
            label="The company"
            choices={TARGET_CHOICES}
            value={target}
            onChange={(id) => onChange({ target: id as GrantDraft['target'] })}
          />
        </div>
      )}

      {target === 'held' ? (
        <Field id={`${field}-company`} label="Company" width="set">
          <select
            id={`${field}-company`}
            value={draft.holdingId}
            onChange={(e) => onChange({ holdingId: e.target.value })}
            required
          >
            <option value="">Choose…</option>
            {choices.map((choice) => (
              <option key={choice.instrumentId} value={String(choice.instrumentId)}>
                {choice.symbol} — {choice.name}
              </option>
            ))}
          </select>
          <small>
            Everything already entered, whether a position names it or only another grant does.
          </small>
        </Field>
      ) : (
        <>
          <div className="nw-grant-row">
            <Field id={`${field}-company-name`} label="Company name" width="set">
              <input
                id={`${field}-company-name`}
                value={draft.companyName}
                onChange={(e) => onChange({ companyName: e.target.value })}
                required
              />
            </Field>
            <Field id={`${field}-company-currency`} label="Priced in" width="set">
              <input
                id={`${field}-company-currency`}
                value={draft.companyCurrency}
                onChange={(e) => onChange({ companyCurrency: e.target.value })}
                maxLength={3}
                required
              />
            </Field>
            <Field id={`${field}-company-symbol`} label="Symbol" width="set">
              <input
                id={`${field}-company-symbol`}
                value={draft.companySymbol}
                onChange={(e) => onChange({ companySymbol: e.target.value })}
              />
              <small>Worked out from the name if left blank.</small>
            </Field>
          </div>
          <p>
            The company is recorded as unlisted and holds nothing: a warrant over it is not a share
            in it. Until a price per share is entered against it, the report says the grant needs an
            input rather than that it is worth nothing.
          </p>
        </>
      )}
    </>
  )
}
